import { useState } from "react";
import { Link } from "react-router-dom";
import { submitContact } from "../lib/api";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Mail, Phone, MapPin, Send, CheckCircle, ArrowRight } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "", type: "general" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#faf8f3]/80 border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between h-16">
          <Logo />
          <div className="flex items-center gap-6">
            <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]">Browse</Link>
            <Link to="/auth">
              <Button className="rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white px-5 text-sm">Sign In</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-5 md:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left — Info */}
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-[#0d0d0d] font-['Fraunces'] mb-4" data-testid="contact-heading">
              Get in touch
            </h1>
            <p className="text-[#888] mb-10 leading-relaxed">
              Have a question, need support, or want to list your property? We'd love to hear from you.
            </p>

            <div className="space-y-6">
              <a href="mailto:hello@lowkey.rent" className="flex items-start gap-4 group" data-testid="email-hello">
                <div className="w-11 h-11 rounded-xl bg-[#1a6b3c]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1a6b3c]/20 transition-colors">
                  <Mail className="w-5 h-5 text-[#1a6b3c]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0d0d0d] text-sm">General Inquiries</p>
                  <p className="text-[#1a6b3c] font-medium text-sm group-hover:underline">hello@lowkey.rent</p>
                  <p className="text-xs text-[#888] mt-0.5">Partnerships, press, and general questions</p>
                </div>
              </a>

              <a href="mailto:support@lowkey.rent" className="flex items-start gap-4 group" data-testid="email-support">
                <div className="w-11 h-11 rounded-xl bg-[#e8633a]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#e8633a]/20 transition-colors">
                  <Mail className="w-5 h-5 text-[#e8633a]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0d0d0d] text-sm">Support</p>
                  <p className="text-[#e8633a] font-medium text-sm group-hover:underline">support@lowkey.rent</p>
                  <p className="text-xs text-[#888] mt-0.5">Account issues, payments, disputes</p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#0d0d0d]/5 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#0d0d0d]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0d0d0d] text-sm">Serving the DMV</p>
                  <p className="text-xs text-[#888] mt-0.5">
                    <strong>VA</strong> — Norfolk · Virginia Beach · Suffolk · Chesapeake · Newport News · Hampton · Portsmouth · Williamsburg · Arlington · Alexandria · Falls Church · Springfield · Fairfax · McLean · Reston · Herndon · Vienna · Tysons · Centreville · Chantilly · Manassas · Woodbridge · Dumfries · Dale City · Quantico · Fort Belvoir · Stafford · Fredericksburg · Annandale · Burke · Lorton · Mount Vernon · Great Falls · Oakton · Gainesville · Haymarket · Bristow · Ashburn · Leesburg · Sterling · Purcellville · Warrenton · Culpeper · Front Royal · Winchester<br />
                    <strong>DC</strong> — Washington<br />
                    <strong>MD</strong> — Bethesda · Silver Spring · Rockville · Gaithersburg · Germantown · College Park · Bowie · Largo · Laurel · Greenbelt · Takoma Park · Frederick · Waldorf
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div>
            {sent ? (
              <div className="bg-white rounded-2xl border border-black/[0.06] p-8 text-center" data-testid="contact-success">
                <div className="w-14 h-14 rounded-2xl bg-[#1a6b3c]/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-7 h-7 text-[#1a6b3c]" />
                </div>
                <h2 className="text-2xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2">Message sent!</h2>
                <p className="text-[#888] text-sm mb-6">We'll get back to you within 24 hours.</p>
                <Button onClick={() => { setSent(false); setForm({ name: "", email: "", message: "", type: "general" }); }}
                  variant="outline" className="rounded-full border-black/[0.08] text-sm">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-black/[0.06] p-6 sm:p-8 space-y-4" data-testid="contact-form">
                <h2 className="font-black text-[#0d0d0d] font-['Fraunces'] text-xl mb-1">Send us a message</h2>
                <p className="text-xs text-[#888] mb-4">We typically respond within 24 hours.</p>

                <div>
                  <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">What's this about?</label>
                  <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                    <SelectTrigger className="rounded-xl border-black/[0.08]" data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Question</SelectItem>
                      <SelectItem value="support">Account / Payment Support</SelectItem>
                      <SelectItem value="listing_inquiry">Listing Inquiry</SelectItem>
                      <SelectItem value="partnership">Partnership / Press</SelectItem>
                      <SelectItem value="bug_report">Bug Report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Name</label>
                    <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Your name" className="rounded-xl border-black/[0.08] h-11" required />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Email</label>
                    <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="you@example.com" className="rounded-xl border-black/[0.08] h-11" required />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Message</label>
                  <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Tell us what's on your mind..." className="rounded-xl border-black/[0.08] min-h-[120px]" required />
                </div>

                {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="contact-error">{error}</p>}

                <Button data-testid="contact-submit" type="submit" disabled={loading}
                  className="w-full h-12 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold text-sm">
                  {loading ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-black/[0.06] mt-16">
        <div className="max-w-5xl mx-auto px-5 md:px-12 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-8">
            <div>
              <Logo size="small" />
              <p className="text-xs text-[#888] mt-3 leading-relaxed">Rooms, apartments & houses — verified, background-checked, flexible leases.</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#0d0d0d] uppercase tracking-wider mb-3">Quick Links</p>
              <div className="space-y-2">
                <Link to="/browse" className="block text-sm text-[#888] hover:text-[#1a6b3c]">Browse Listings</Link>
                <Link to="/auth" className="block text-sm text-[#888] hover:text-[#1a6b3c]">Sign In / Register</Link>
                <Link to="/contact" className="block text-sm text-[#888] hover:text-[#1a6b3c]">Contact Us</Link>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#0d0d0d] uppercase tracking-wider mb-3">Contact</p>
              <div className="space-y-2">
                <a href="mailto:hello@lowkey.rent" className="block text-sm text-[#888] hover:text-[#1a6b3c]">hello@lowkey.rent</a>
                <a href="mailto:support@lowkey.rent" className="block text-sm text-[#888] hover:text-[#1a6b3c]">support@lowkey.rent</a>
              </div>
            </div>
          </div>
          <div className="border-t border-black/[0.06] pt-6 flex items-center justify-between">
            <p className="text-xs text-[#888]">&copy; 2026 Low Key Rents. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
